import { useEffect, useState } from 'react'
import {
  changeEmail, changePassword, changePhone, closeAccount, fetchProfile, getProfile,
  requestCode, signOut, updateIdentity, verifyCode, type AccountProfile,
} from '../db/auth'
import { db } from '../db/database'
import { formatMonth, formatFullDate } from '../lib/dates'
import { Icon, type IconName } from './Icon'
import './AccountCard.css'
import {
  AIDE_TELEPHONE, MAX_NOM, MIN_DATE, MIN_MOT_DE_PASSE, MOTIF_TELEPHONE,
  formaterTelephone, messageEmail, messageTelephone,
} from '../lib/limites'

/**
 * Le compte, en tête du Profil — décision 2026-08-24 (soir).
 *
 * L'organisation suit la maquette : une identité en bandeau, puis « Mes
 * informations » — une ligne par donnée, chacune avec son icône. C'est la forme
 * d'une fiche qu'on relit, pas d'un formulaire qu'on remplit : l'écran répond
 * d'abord à « qu'est-ce que l'app sait de moi ? », et la modification n'arrive
 * qu'ensuite, sur demande.
 *
 * ⚠️ **UN seul numéro et UN seul email par compte** — décision 2026-08-24. La
 * maquette montrait des listes avec un identifiant « principal » ; ce n'est pas
 * le modèle. Le téléphone est l'ANCRE d'identité (les features sociales se
 * croisent par numéro), et deux numéros pour un compte rouvriraient la question
 * de savoir lequel fait foi.
 *
 * Tout ici exige le réseau, contrairement au reste de l'app. L'écran le dit
 * quand ça échoue plutôt que de laisser croire à un enregistrement.
 */
type Volet = null | 'identite' | 'motdepasse' | 'telephone' | 'email' | 'suppression'

export function AccountCard({ onSignedOut }: { onSignedOut: () => void }) {
  const [profil, setProfil] = useState<AccountProfile | null>(getProfile)
  const [volet, setVolet] = useState<Volet>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [succes, setSucces] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)

  // Relu au serveur à l'ouverture : le profil local peut dater d'avant une
  // confirmation faite ailleurs — dans le panneau d'administration, par exemple.
  useEffect(() => { void fetchProfile().then((r) => { if (r.ok) setProfil(r.data) }) }, [])

  function ouvrir(cible: Volet) {
    setVolet(volet === cible ? null : cible)
    setSucces(null)
    setErreur(null)
  }

  function annonce(result: { ok: boolean; error?: string }, message: string) {
    setOccupe(false)
    if (!result.ok) { setErreur(result.error ?? 'Réessaie.'); return false }
    setErreur(null)
    setSucces(message)
    setVolet(null)
    return true
  }

  if (!profil) {
    return (
      <section className="card account-card">
        <h2>Compte</h2>
        <p className="card-help">Cet appareil n'est rattaché à aucun compte.</p>
      </section>
    )
  }

  const nom = [profil.first_name, profil.last_name].filter(Boolean).join(' ')

  return (
    <section className="card account-card">
      {/* Le bandeau d'identité : qui, joignable où, et depuis quand. */}
      <header className="account-head">
        <span className="account-avatar" aria-hidden="true">
          {(profil.first_name?.[0] ?? '?').toUpperCase()}
        </span>
        <div className="account-identity">
          <strong>{nom || 'Sans nom'}</strong>
          <span className="tabular">{profil.phone}</span>
          {profil.created_at && (
            <span className="account-since">
              <Icon name="calendar" size={13} />
              Membre depuis {formatMonth(profil.created_at.slice(0, 10))}
            </span>
          )}
        </div>
      </header>

      {erreur && <p className="account-error" role="alert">{erreur}</p>}
      {succes && <p className="account-ok" role="status">{succes}</p>}

      <div className="account-block">
        <div className="account-block-head">
          <h3>Mes informations</h3>
          <button type="button" className="account-edit" onClick={() => ouvrir('identite')}>
            <Icon name="pencil" size={14} /> Modifier mes informations
          </button>
        </div>

        {volet === 'identite' ? (
          <Identite profil={profil} occupe={occupe}
                    onCancel={() => setVolet(null)}
                    onSubmit={async (valeurs) => {
                      setOccupe(true)
                      const result = await updateIdentity(valeurs)
                      if (annonce(result, 'Informations enregistrées.') && result.ok) {
                        setProfil(result.data)
                      }
                    }} />
        ) : (
          <dl className="account-facts">
            <Fait icone="person" nom="Nom et prénom" valeur={nom || '—'} />
            <Fait icone="mail" nom="Email"
                  valeur={profil.email ?? 'Aucun email'}
                  vide={!profil.email}
                  etat={profil.email ? (profil.email_verified ? 'ok' : 'attente') : undefined}
                  onEdit={() => ouvrir('email')} />
            <Fait icone="phone" nom="Téléphone" valeur={profil.phone}
                  etat={profil.phone_verified ? 'ok' : 'attente'}
                  onEdit={() => ouvrir('telephone')} />
            <Fait icone="calendar" nom="Date de naissance"
                  valeur={profil.birthdate ? formatFullDate(profil.birthdate) : '—'}
                  vide={!profil.birthdate} />
          </dl>
        )}

        {volet === 'telephone' && (
          <Identifiant
            titre="Nouveau numéro" type="tel" defaut={profil.phone} occupe={occupe}
            aide="Un code te sera envoyé pour confirmer ce numéro. Tant qu'il n'est pas confirmé, tu ne pourras pas te reconnecter avec."
            onCancel={() => setVolet(null)}
            onSubmit={async (valeur) => {
              setOccupe(true)
              const result = await changePhone(valeur)
              if (result.ok) setProfil(result.data)
              annonce(result, 'Numéro changé. Entre le code reçu pour le confirmer.')
            }} />
        )}

        {volet === 'email' && (
          <Identifiant
            titre="Email" type="email" defaut={profil.email ?? ''} occupe={occupe}
            aide="Laisse vide pour retirer ton email. Sinon, un code te sera envoyé pour le confirmer."
            onCancel={() => setVolet(null)}
            onSubmit={async (valeur) => {
              setOccupe(true)
              const result = await changeEmail(valeur)
              if (result.ok) setProfil(result.data)
              annonce(result, valeur ? 'Email changé. Entre le code reçu.' : 'Email retiré.')
            }} />
        )}

        {/* La confirmation se fait ICI, sans repasser par le portail : on vient
            de changer son numéro, c'est le moment de le prouver. */}
        {(!profil.phone_verified || (profil.email && !profil.email_verified)) && (
          <Confirmation profil={profil}
                        onDone={async () => {
                          const r = await fetchProfile()
                          if (r.ok) setProfil(r.data)
                          setSucces('Confirmé.')
                        }} />
        )}
      </div>

      <div className="account-block">
        <div className="account-block-head">
          <h3>Sécurité</h3>
          <button type="button" className="account-edit" onClick={() => ouvrir('motdepasse')}>
            <Icon name="lock" size={14} /> Changer mon mot de passe
          </button>
        </div>

        {volet === 'motdepasse' && (
          <MotDePasse occupe={occupe} onCancel={() => setVolet(null)}
                      onSubmit={async (current, password) => {
                        setOccupe(true)
                        annonce(await changePassword(current, password), 'Mot de passe changé.')
                      }} />
        )}

        <button type="button" className="account-signout"
                onClick={() => { signOut(); onSignedOut() }}>
          Me déconnecter
        </button>
        {/* Se déconnecter n'efface RIEN : quelqu'un qui prête son téléphone doit
            pouvoir sortir sans perdre son budget. */}
        <p className="account-note">Tes données restent sur cet appareil.</p>
      </div>

      <div className="account-danger">
        {volet === 'suppression' ? (
          <Suppression occupe={occupe}
                       onCancel={() => setVolet(null)}
                       onSubmit={async (password) => {
                         setOccupe(true)
                         const result = await closeAccount(password)
                         setOccupe(false)
                         if (!result.ok) return setErreur(result.error)
                         // Le compte fermé, la base locale n'a plus de
                         // propriétaire : la garder laisserait l'argent de
                         // quelqu'un sur un appareil qu'il croit vidé.
                         signOut()
                         db.close()
                         await db.delete()
                         onSignedOut()
                       }} />
        ) : (
          <button type="button" className="account-delete" onClick={() => ouvrir('suppression')}>
            <Icon name="trash" size={14} /> Supprimer mon compte
          </button>
        )}
      </div>
    </section>
  )
}

/** Une ligne de la fiche : icône, intitulé, valeur, et son état s'il en a un. */
function Fait(
  { icone, nom, valeur, etat, vide, onEdit }: {
    icone: IconName
    nom: string
    valeur: string
    /** Un identifiant se prouve : tant qu'il ne l'est pas, l'écran le dit. */
    etat?: 'ok' | 'attente'
    vide?: boolean
    onEdit?: () => void
  },
) {
  return (
    <div className="account-fact">
      <dt><Icon name={icone} size={16} /> {nom}</dt>
      <dd className={vide ? 'is-empty' : ''}>
        <span>{valeur}</span>
        {etat && (
          <span className={`account-state is-${etat}`}>
            {etat === 'ok'
              ? <><Icon name="check" size={11} /> Confirmé</>
              : 'À confirmer'}
          </span>
        )}
      </dd>
      {onEdit && (
        <button type="button" aria-label={`Modifier ${nom.toLowerCase()}`} onClick={onEdit}>
          <Icon name="pencil" size={14} />
        </button>
      )}
    </div>
  )
}

function Identite(
  { profil, occupe, onCancel, onSubmit }: {
    profil: AccountProfile
    occupe: boolean
    onCancel: () => void
    onSubmit: (v: { first_name: string; last_name: string; birthdate: string }) => void
  },
) {
  const [first, setFirst] = useState(profil.first_name ?? '')
  const [last, setLast] = useState(profil.last_name ?? '')
  const [birth, setBirth] = useState(profil.birthdate ?? '')

  return (
    <form className="account-form"
          onSubmit={(e) => { e.preventDefault(); onSubmit({ first_name: first, last_name: last, birthdate: birth }) }}>
      <label><span>Prénom</span>
        <input type="text" required maxLength={MAX_NOM}
               value={first} onChange={(e) => setFirst(e.target.value)} /></label>
      <label><span>Nom</span>
        <input type="text" required maxLength={MAX_NOM}
               value={last} onChange={(e) => setLast(e.target.value)} /></label>
      <label>
        {/* Une DATE et non un âge : un âge est faux dès le lendemain de
            l'anniversaire, et rien ne le remettrait à jour. */}
        <span>Date de naissance</span>
        <input type="date" required min={MIN_DATE}
               max={new Date().toISOString().slice(0, 10)}
               value={birth} onChange={(e) => setBirth(e.target.value)} /></label>
      <div className="account-form-actions">
        <button type="submit" className="is-primary" disabled={occupe}>
          {occupe ? 'Envoi…' : 'Enregistrer'}
        </button>
        <button type="button" onClick={onCancel}>Annuler</button>
      </div>
    </form>
  )
}

function MotDePasse(
  { occupe, onCancel, onSubmit }: {
    occupe: boolean; onCancel: () => void; onSubmit: (c: string, p: string) => void
  },
) {
  const [current, setCurrent] = useState('')
  const [password, setPassword] = useState('')

  return (
    <form className="account-form"
          onSubmit={(e) => { e.preventDefault(); onSubmit(current, password) }}>
      <label>
        {/* L'ancien est exigé même avec une session valide : un téléphone
            déverrouillé laissé une minute suffirait autrement à voler le
            compte, jeton compris. */}
        <span>Mot de passe actuel</span>
        <input type="password" autoComplete="current-password" required
               value={current} onChange={(e) => setCurrent(e.target.value)} /></label>
      <label><span>Nouveau mot de passe <em>({MIN_MOT_DE_PASSE} caractères minimum)</em></span>
        <input type="password" autoComplete="new-password" required minLength={MIN_MOT_DE_PASSE}
               value={password} onChange={(e) => setPassword(e.target.value)} /></label>
      <div className="account-form-actions">
        <button type="submit" className="is-primary" disabled={occupe}>
          {occupe ? 'Envoi…' : 'Changer'}
        </button>
        <button type="button" onClick={onCancel}>Annuler</button>
      </div>
    </form>
  )
}

function Identifiant(
  { titre, type, defaut, aide, occupe, onCancel, onSubmit }: {
    titre: string; type: 'tel' | 'email'; defaut: string; aide: string
    occupe: boolean; onCancel: () => void; onSubmit: (v: string) => void
  },
) {
  const [valeur, setValeur] = useState(defaut)

  // Le numéro se met en forme au fil de la frappe (`034 12 345 67`), l'email
  // non : y insérer des espaces le rendrait intapable.
  const faute = type === 'tel'
    ? messageTelephone(valeur, { obligatoire: true })
    : messageEmail(valeur)

  return (
    <form className="account-form"
          onSubmit={(e) => { e.preventDefault(); if (!faute) onSubmit(valeur) }}>
      <label><span>{titre}</span>
        <input type={type === 'tel' ? 'text' : 'email'} required={type === 'tel'}
               inputMode={type === 'tel' ? 'tel' : 'email'}
               placeholder={type === 'tel' ? '034 12 345 67' : 'nom@exemple.mg'}
               maxLength={type === 'tel' ? 14 : 255}
               {...(type === 'tel'
                 ? { pattern: MOTIF_TELEPHONE, title: AIDE_TELEPHONE }
                 : {})}
               value={valeur}
               onChange={(e) => setValeur(
                 type === 'tel' ? formaterTelephone(e.target.value) : e.target.value)} /></label>
      {faute && <p className="account-erreur" role="alert">{faute}</p>}
      <p className="account-note">{aide}</p>
      <div className="account-form-actions">
        <button type="submit" className="is-primary" disabled={occupe || faute !== null}>
          {occupe ? 'Envoi…' : 'Enregistrer'}
        </button>
        <button type="button" onClick={onCancel}>Annuler</button>
      </div>
    </form>
  )
}

/** Prouver un canal qui vient de changer, sans repasser par le portail. */
function Confirmation({ profil, onDone }: { profil: AccountProfile; onDone: () => void }) {
  const canal: 'sms' | 'email' = profil.phone_verified ? 'email' : 'sms'
  const cible = canal === 'sms' ? profil.phone : (profil.email ?? '')
  const [code, setCode] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  return (
    <form className="account-confirm"
          onSubmit={async (e) => {
            e.preventDefault()
            const result = await verifyCode(cible, code, canal)
            if (!result.ok) return setMessage(result.error)
            setCode('')
            onDone()
          }}>
      <p className="account-note">
        {canal === 'sms' ? 'Confirme ton numéro' : 'Confirme ton email'} : <strong>{cible}</strong>
      </p>
      {message && <p className="account-error" role="alert">{message}</p>}
      <div className="account-confirm-row">
        <input type="text" inputMode="numeric" className="tabular" placeholder="000000"
               maxLength={6} value={code}
               onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
        <button type="submit" disabled={code.length < 6}>Confirmer</button>
        <button type="button" className="account-resend"
                onClick={() => void requestCode(cible, canal).then(() => setMessage('Nouveau code demandé.'))}>
          Renvoyer
        </button>
      </div>
    </form>
  )
}

function Suppression(
  { occupe, onCancel, onSubmit }: {
    occupe: boolean; onCancel: () => void; onSubmit: (password: string) => void
  },
) {
  const [password, setPassword] = useState('')

  return (
    <form className="account-form is-danger"
          onSubmit={(e) => { e.preventDefault(); onSubmit(password) }}>
      <p className="account-warn">
        <strong>C'est définitif.</strong> Ton compte sera fermé, cet appareil
        vidé, et tes opérations ne seront plus accessibles. Ton numéro te reste
        réservé — personne d'autre ne pourra le reprendre.
      </p>
      <label><span>Confirme avec ton mot de passe</span>
        <input type="password" autoComplete="current-password" required
               value={password} onChange={(e) => setPassword(e.target.value)} /></label>
      <div className="account-form-actions">
        <button type="submit" className="is-danger" disabled={occupe}>
          {occupe ? 'Suppression…' : 'Supprimer définitivement'}
        </button>
        <button type="button" onClick={onCancel}>Annuler</button>
      </div>
    </form>
  )
}
